import { useState, type FocusEvent, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import styles from "./BarChart.module.css";

export interface BarChartDataPoint {
  label: string;
  tooltipLabel?: string;
  value: number;
}

export interface BarChartProps {
  data: BarChartDataPoint[];
  valueFormatter?: (value: number) => string;
  "aria-label"?: string;
  className?: string;
}

const CHART_W = 600;
const CHART_H = 180;
const PAD_TOP = 8;
const PAD_SIDE = 4;
const LABEL_H = 28;
const TOTAL_H = PAD_TOP + CHART_H + LABEL_H;
const TOOLTIP_W = 144;
const TOOLTIP_H = 50;

interface TooltipState {
  label: string;
  value: string;
  left: number;
  top: number;
}

function showLabel(index: number, total: number): boolean {
  if (total <= 15) return true;
  if (total <= 31) return index % 7 === 0 || index === total - 1;
  return false;
}

export function BarChart({ data, valueFormatter, "aria-label": ariaLabel, className }: BarChartProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  if (!data.length) {
    return <div className={clsx(styles.empty, className)}>Žádná data</div>;
  }

  const max = Math.max(...data.map((d) => d.value), 1);
  const n = data.length;
  const usableW = CHART_W - PAD_SIDE * 2;
  const barW = Math.max(2, (usableW - Math.max(0, n - 1) * 2) / n);
  const gap = n > 1 ? (usableW - n * barW) / (n - 1) : 0;

  function showTooltip(target: SVGRectElement, label: string, value: string) {
    const rect = target.getBoundingClientRect();
    const left = Math.min(
      window.innerWidth - TOOLTIP_W / 2 - 8,
      Math.max(TOOLTIP_W / 2 + 8, rect.left + rect.width / 2),
    );
    const top = Math.max(8, rect.top - TOOLTIP_H - 8);
    setTooltip({ label, value, left, top });
  }

  function handleBarMouseEnter(
    e: MouseEvent<SVGRectElement>,
    label: string,
    value: string,
  ) {
    showTooltip(e.currentTarget, label, value);
  }

  function handleBarFocus(
    e: FocusEvent<SVGRectElement>,
    label: string,
    value: string,
  ) {
    showTooltip(e.currentTarget, label, value);
  }

  return (
    <>
      <svg
        viewBox={`0 0 ${CHART_W} ${TOTAL_H}`}
        className={clsx(styles.chart, className)}
        role="img"
        aria-label={ariaLabel ?? "Graf"}
      >
        {data.map((d, i) => {
          const x = PAD_SIDE + i * (barW + gap);
          const barH = Math.max(2, (d.value / max) * CHART_H);
          const y = PAD_TOP + CHART_H - barH;
          const label = d.tooltipLabel ?? d.label;
          const value = valueFormatter ? valueFormatter(d.value) : String(d.value);
          const title = `${label}: ${value}`;

          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={barH}
                className={styles.bar}
                tabIndex={0}
                aria-label={title}
                onMouseEnter={(e) => handleBarMouseEnter(e, label, value)}
                onMouseLeave={() => setTooltip(null)}
                onFocus={(e) => handleBarFocus(e, label, value)}
                onBlur={() => setTooltip(null)}
              >
                <title>{title}</title>
              </rect>
              {showLabel(i, n) && (
                <text
                  x={x + barW / 2}
                  y={TOTAL_H - 4}
                  fontSize={9}
                  textAnchor="middle"
                  style={{ fill: "var(--ink-dim, #999)" }}
                >
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {tooltip &&
        createPortal(
          <div
            className={styles.portalTooltip}
            style={{ left: tooltip.left, top: tooltip.top }}
            role="tooltip"
          >
            <div className={styles.tooltipLabel}>{tooltip.label}</div>
            <div className={styles.tooltipValue}>{tooltip.value}</div>
          </div>,
          document.body,
        )}
    </>
  );
}
